import { AxiosPromise } from 'axios';

import { axios } from 'src/app/utils';
import { urlsConfig } from 'src/configs';
import { queryTypes, serviceDeliverableTypes } from 'src/types';
import { fileHelper, urlHelper } from '../../helpers';

export const lookupDeliverableTable = ({
  limit,
  offset,
  ids,
  value,
  order,
  sortOrder,
  blockSpinner,
}: serviceDeliverableTypes.DeliverableLookupDataType): AxiosPromise<serviceDeliverableTypes.DeliverableTableLookupResultType> => {
  const query: queryTypes.LookupQueryParametersType = {};

  if (ids) {
    query.ids = ids.join(',');
  }

  if (value) {
    query.search = value;
  }

  if (order && sortOrder) {
    query.order = order;
    query.sortOrder = sortOrder;
  }

  if (limit && offset) {
    query.limit = limit;
    query.offset = offset;
  }

  return axios.get(urlHelper.addQueryParameters(query, urlsConfig.deliverable.lookupDeliverable), {
    blockSpinner,
  });
};

export const lookupDeliverableWithEvent = ({
  limit,
  offset,
  ids,
  value,
  order,
  eventIds,
  sortOrder,
  blockSpinner,
}: serviceDeliverableTypes.DeliverableLookupDataType): AxiosPromise<serviceDeliverableTypes.DeliverableTableLookupResultType> => {
  const query: queryTypes.LookupQueryParametersType = {};

  if (ids) {
    query.ids = ids.join(',');
  }

  if (value) {
    query.search = value;
  }

  if (order && sortOrder) {
    query.order = order;
    query.sortOrder = sortOrder;
  }

  if (eventIds) {
    return axios.get(`${urlsConfig.deliverable.lookupDeliverable}?eventIds=${eventIds}`, {
      blockErrorHandler: true,
    });
  }

  if (limit && offset) {
    query.limit = limit;
    query.offset = offset;
  }

  return axios.get(urlHelper.addQueryParameters(query, urlsConfig.deliverable.lookupDeliverable), {
    blockSpinner,
  });
};

export const lookupDeliverableOfOntology = ({
  limit,
  offset,
  ids,
  value,
  order,
  sortOrder,
  blockSpinner,
  load,
}: serviceDeliverableTypes.DeliverableLookupDataType): AxiosPromise<serviceDeliverableTypes.DeliverableOfOntologyLookupResultType> => {
  const query: queryTypes.LookupQueryParametersType = {};

  if (ids) {
    query.ids = ids.join(',');
  }

  if (value) {
    query.search = value;
  }

  if (order && sortOrder) {
    query.order = order;
    query.sortOrder = sortOrder;
  }

  if (limit && offset) {
    query.limit = limit;
    query.offset = offset;
  }

  return axios.get(urlHelper.addQueryParameters(query, urlsConfig.deliverable.lookupDeliverableOfOntology), {
    blockSpinner,
  });
};

export const createDeliverable = (
  form: serviceDeliverableTypes.DeliverableCreateDataType,
  // formData: FormData,
  options: { blockErrorHandler: boolean },
): AxiosPromise<serviceDeliverableTypes.DeliverableCreateResultType> => {
  const entitiesData = fileHelper.convertArrayData(form.relatedAudienceEntities);
  const processesData = fileHelper.convertArrayData(form.relatedAudienceProcesses);

  const formData = fileHelper.convertDataURLsToFormData(form.attachments);

  if (form.eventId) {
    formData.append('eventId', form.eventId);
  }

  if (form.taskNumberId) {
    formData.append('taskNumberId', form.taskNumberId);
  }

  if (form.deliverableNumber) {
    formData.append('deliverableNumber', form.deliverableNumber);
  }

  if (form.deliverableName) {
    formData.append('deliverableName', form.deliverableName);
  }

  if (form.relatedProjectInput) {
    formData.append('relatedProjectInput', form.relatedProjectInput);
  }

  // if (form.relatedProjectInputDateTime) {
  //   formData.append('relatedProjectInputDateTime', form.relatedProjectInputDateTime.toString());
  // }

  if (form.relatedKanban) {
    formData.append('relatedKanban', form.relatedKanban);
  }

  // if (form.relatedProjectInputDateTime) {
  //   formData.append('relatedProjectInputDateTime', form.relatedProjectInputDateTime.toString());
  // }
  if (form.timeDetails) {
    formData.append('timeDetails', JSON.stringify(form.timeDetails));
  }

  if (form.type) {
    formData.append('type', form.type.toString());
  }

  if (form.relatedAudienceEntities) {
    form.relatedAudienceEntities.forEach((e) => formData.append('relatedAudienceEntities', e as any as string));

    // formData.append('relatedAudienceEntities', entitiesData);
  }

  if (form.relatedAudienceProcesses) {
    form.relatedAudienceProcesses.forEach((e) => formData.append('relatedAudienceProcesses', e as any as string));
    // formData.append('relatedAudienceProcesses', processesData);
  }

  if (form.workloadOnAudience) {
    formData.append('workloadOnAudience', form.workloadOnAudience.toString());
  }

  if (form.deliverableDirection) {
    formData.append('deliverableDirection', form.deliverableDirection);
  }

  if (form.weightInTOAchievement) {
    formData.append('weightInTOAchievement', form.weightInTOAchievement.toString());
  }

  if (form.description) {
    formData.append('description', form.description);
  }

  if (form.coordinationRemarks) {
    formData.append('coordinationRemarks', form.coordinationRemarks);
  } else {
    formData.append('coordinationRemarks', '');
  }

  if (form.log) {
    formData.append('log', form.log);
  }

  if (form.comment) {
    formData.append('comment', form.comment);
  } else {
    formData.append('comment', '');
  }

  if (form.deliverableStatusId) {
    formData.append('deliverableStatusId', form.deliverableStatusId);
  }
  formData.append('isInternal', form.isInternal.toString());

  return axios.post(urlsConfig.deliverable.createDeliverable, formData, { blockErrorHandler: true });
};

export const deliverableEdit = (
  form: serviceDeliverableTypes.DeliverableEditDataType,
  // formData: FormData,
): AxiosPromise<serviceDeliverableTypes.DeliverableEditResultType> => {
  const entitiesData = fileHelper.convertArrayData(form.relatedAudienceEntities);
  const processesData = fileHelper.convertArrayData(form.relatedAudienceProcesses);

  const formData = fileHelper.convertDataURLsToFormData(form.attachments);

  if (form.id !== undefined) {
    formData.append('id', form.id);
  }

  if (form.deletedAttachments !== undefined) {
    form.deletedAttachments.forEach((eachAttachment: any) => {
      formData.append('deletedAttachments', eachAttachment);
    });
  }

  if (form.eventId) {
    formData.append('eventId', form.eventId);
  }

  if (form.taskNumberId) {
    formData.append('taskNumberId', form.taskNumberId);
  }

  if (form.deliverableNumber) {
    formData.append('deliverableNumber', form.deliverableNumber);
  }

  if (form.deliverableName) {
    formData.append('deliverableName', form.deliverableName);
  }

  if (form.relatedProjectInput) {
    formData.append('relatedProjectInput', form.relatedProjectInput);
  }

  // if (form.relatedProjectInputDateTime) {
  //   formData.append('relatedProjectInputDateTime', form.relatedProjectInputDateTime.toString());
  // }

  if (form.relatedKanban) {
    formData.append('relatedKanban', form.relatedKanban);
  }
  if (form.timeDetails) {
    formData.append('timeDetails', JSON.stringify(form.timeDetails));
  }

  if (form.type) {
    formData.append('type', form.type.toString());
  }

  // if (form.relatedKanbanDateTime) {
  //   formData.append('relatedKanbanDateTime', form.relatedKanbanDateTime.toString());
  // }

  if (form.relatedAudienceEntities) {
    form.relatedAudienceEntities.forEach((e) => formData.append('relatedAudienceEntities', e as any as string));

    // formData.append('relatedAudienceEntities', entitiesData);
  }

  if (form.relatedAudienceProcesses) {
    form.relatedAudienceProcesses.forEach((e) => formData.append('relatedAudienceProcesses', e as any as string));
    // formData.append('relatedAudienceProcesses', processesData);
  }

  if (form.workloadOnAudience) {
    formData.append('workloadOnAudience', form.workloadOnAudience.toString());
  }

  if (form.weightInTOAchievement) {
    formData.append('weightInTOAchievement', form.weightInTOAchievement.toString());
  }

  if (form.description) {
    formData.append('description', form.description);
  } else {
    formData.append('description', '');
  }

  if (form.coordinationRemarks) {
    formData.append('coordinationRemarks', form.coordinationRemarks);
  } else {
    formData.append('coordinationRemarks', '');
  }

  if (form.log) {
    formData.append('log', form.log);
  }

  if (form.comment) {
    formData.append('comment', form.comment);
  } else {
    formData.append('comment', '');
  }

  if (form.deliverableStatusId) {
    formData.append('deliverableStatusId', form.deliverableStatusId);
  }
  if (form.deliverableDirection) {
    formData.append('deliverableDirection', form.deliverableDirection);
  }
  formData.append('isInternal', form.isInternal.toString());

  return axios.patch(urlsConfig.deliverable.deliverableEdit, formData, { blockErrorHandler: true });
};

export const deliverableDelete = ({
  id,
}: serviceDeliverableTypes.DeliverableDeleteDataType): AxiosPromise<serviceDeliverableTypes.DeliverableDeleteResultType> => {
  return axios.delete(`${urlsConfig.deliverable.deliverableDelete}/${id}`);
};
